import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  getOutletProductSettings,
  compressImageFile,
  updateProductSettings,
} from "../../api/outletProductSettingApi";
import ModuleHeader, { ModuleHeaderBack } from "../../components/ModuleHeader/ModuleHeader";
import ModulePageShell from "../../components/ModulePageShell/ModulePageShell";
import "./product-form.scss";

const emptyForm = {
  name: "", description: "", price: "", normal_price: "",
  pickup_start_time: "", pickup_end_time: "", available_start_time: "",
  available_end_time: "", image: "",
};

export default function ProductFormPage() {
  const navigate = useNavigate();
  const { productId } = useParams();
  const { selectedOutlet } = useOutletContext();
  const imageInputRef = useRef(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!selectedOutlet?.id) return;
    setLoading(true);
    getOutletProductSettings(selectedOutlet.id)
      .then((data) => {
        if (!data.product || String(data.product.id) !== String(productId)) {
          throw new Error("Product not found for the selected outlet.");
        }
        setForm({ ...emptyForm, ...data.product });
      })
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  }, [productId, selectedOutlet?.id]);

  const updateField = (field, value) => {
    setError("");
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      updateField("image", await compressImageFile(file, {
        maxWidth: 1400,
        maxHeight: 1000,
        maxBytes: 500 * 1024,
        quality: 0.84,
      }));
    } catch (imageError) {
      setError(imageError.message);
    }
  };

  const save = async () => {
    const price = Number(form.price);
    const originalPrice = form.normal_price === "" || form.normal_price === null
      ? null
      : Number(form.normal_price);

    if (originalPrice !== null && originalPrice <= price) {
      setError("The original price must be greater than the selling price.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await updateProductSettings(selectedOutlet.id, productId, {
        name: form.name.trim(),
        description: form.description || null,
        price,
        normal_price: originalPrice,
        pickup_start_time: form.pickup_start_time || null,
        pickup_end_time: form.pickup_end_time || null,
        available_end_time: form.available_end_time || null,
        image: form.image || null,
      });
      navigate("/outlet-products");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModulePageShell className="product-form-page">
      <ModuleHeader
        bleed
        title="Edit Product"
        description="Update product details, pricing, pickup window, and availability timing."
        back={
          <ModuleHeaderBack onClick={() => navigate("/outlet-products")}>
            ← Back
          </ModuleHeaderBack>
        }
      />

      <section className="product-form-card">
        <div className="product-form-card__header"><h3>Product Setup</h3><p>Manage the permanent product details for {selectedOutlet?.name || "this outlet"}.</p></div>
        {error ? <div className="product-form-logs__intro"><p>{error}</p></div> : null}
        {loading ? <div className="product-form-tab-panel">Loading product settings...</div> : (
          <div className="product-form-tab-panel">
            <div className="product-form-grid">
              <label className="product-form-grid__full"><span>Product Name</span><input value={form.name || ""} onChange={(event) => updateField("name", event.target.value)} required /></label>
              <label><span>Price (RM)</span><input type="number" min="0" step="0.01" value={form.price ?? ""} onChange={(event) => updateField("price", event.target.value)} /></label>
              <label><span>Original Price (RM)</span><input type="number" min={Number(form.price || 0) + 0.01} step="0.01" value={form.normal_price ?? ""} onChange={(event) => updateField("normal_price", event.target.value)} /><small className="product-form-grid__help">Must be greater than the selling price.</small></label>
              <label><span>Available Start Time</span><input type="time" value={form.available_start_time || ""} disabled readOnly className="product-form-grid__readonly" /><small className="product-form-grid__help">Recorded automatically when the listing becomes available.</small></label>
              <label><span>Available End Time</span><input type="time" value={form.available_end_time || ""} onChange={(event) => updateField("available_end_time", event.target.value)} /></label>
              <label><span>Pickup Start Time</span><input type="time" value={form.pickup_start_time || ""} onChange={(event) => updateField("pickup_start_time", event.target.value)} /></label>
              <label><span>Pickup End Time</span><input type="time" value={form.pickup_end_time || ""} onChange={(event) => updateField("pickup_end_time", event.target.value)} /></label>

              <div className="product-form-grid__full product-form-upload-field">
                <span>Product Image</span>
                <label className={`product-form-image-upload ${form.image ? "has-preview" : ""}`}>
                  <input ref={imageInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleImage} />
                  {form.image ? (
                    <>
                      <img src={form.image} alt="Product preview" />
                      <div className="product-form-image-upload__overlay"><strong>Change Image</strong><small>PNG or JPG, maximum 5 MB.</small></div>
                      <button type="button" className="product-form-image-upload__remove" onClick={(event) => { event.preventDefault(); updateField("image", ""); }}>×</button>
                    </>
                  ) : <div className="product-form-image-upload__empty"><strong>Upload Image</strong><small>Recommended food image, PNG or JPG.</small></div>}
                </label>
              </div>

              <label className="product-form-grid__full"><span>Description</span><textarea rows="5" value={form.description || ""} onChange={(event) => updateField("description", event.target.value)} /></label>
            </div>
            <div className="product-form-actions">
              <button type="button" className="product-form-actions__secondary" onClick={() => navigate("/outlet-products")}>Cancel</button>
              <button type="button" className="product-form-actions__primary" disabled={saving} onClick={save}>{saving ? "Saving..." : "Save Product"}</button>
            </div>
          </div>
        )}
      </section>
    </ModulePageShell>
  );
}
